/* eslint-disable jsdoc/check-alignment */
import type { Props } from '@studio/design-system/dialog';
import { Message, Prompt } from '@studio/design-system/prompt';
import type { ReactElement } from 'react';
import { usePrefState } from '#~/preferences/hooks.js';

/** @internal */
export type ControlProtectionDialogProps = Props & {
  /** Called when the dialog is confirmed. */
  onConfirm: () => void;

  /** Called when the dialog is dismissed. */
  onDismiss: () => void;
};

/** @internal */
export const ControlProtectionDialog = ({
  onConfirm,
  onDismiss
}: ControlProtectionDialogProps): ReactElement => {
  const [skipControlProtection, setSkipControlProtection] = usePrefState<boolean>(
    'autotracking.skipControlProtection',
    false
  );

  return (
    <div>
      <Prompt
        cancel={{ onClick: onDismiss }}
        className='w-[425px]'
        message={
          <Message>
            {/**
             * This input is used to prevent the dialog from closing when the user presses the space bar which
             * is a common interaction when trying to use the joystick.
             */}
            <input autoFocus className='absolute opacity-0' type='checkbox' />
            <div className='mb-16'>
              <span>
                Switching to another tool while Auto Tracking is in progress will cause tracking to
                stop. Would you like to continue with your selection?
              </span>
            </div>
            <div className='inline rounded bg-secondary/10 p-8'>
              <input
                checked={skipControlProtection}
                className='border-1 border-solid border-secondary outline-0'
                id='do-not-show-again-checkbox'
                onChange={(event): void => {
                  const { checked } = event.target;

                  setSkipControlProtection(checked);
                }}
                type='checkbox'
              />
              <label className='ml-6 text-sm' htmlFor='do-not-show-again-checkbox'>
                Don’t show me this again
              </label>
            </div>
          </Message>
        }
        okay={{ label: 'Stop Tracking', onClick: onConfirm }}
        title='Stop Auto Tracking?'
      />
    </div>
  );
};
